import api from '../utils/api';
import { withOfflineFallback, readServiceCatalog } from '../offline';

export interface ServiceCatalogItem {
  id: string;
  name: string;
  category: 'medication' | 'vaccination' | 'procedure' | 'appointment' | 'other';
  basePrice: number;
  unit?: string;
  color?: string | null;
  aliases?: string[];
  idexxTestCodes?: string[]; // IDEXX test code(s) this product bills for
  clinicId?: string;
  isActive: boolean;
  // null/undefined = FIXED (basePrice used as-is). MARKUP derives basePrice from
  // unitCost*(1+markupPercent/100), floored at minPrice — mirrors the backend
  // resolveSellPrice. basePrice is always the server-persisted resolved price.
  pricingMethod?: 'FIXED' | 'MARKUP' | null;
  unitCost?: number | null;
  markupPercent?: number | null;
  minPrice?: number | null;
  createdAt: string;
  updatedAt: string;
}

export const serviceCatalogService = {
  // Offline-aware AT THE SERVICE so every consumer (appointment-type picker,
  // drug/vaccination/procedure catalogs, the invoice line-item editor, Settings)
  // gets custom catalog prices offline from the synced replica with one wrap.
  // Inert when the offline flag is off (withOfflineFallback returns networkFn).
  getAll: async (params?: { category?: string; isActive?: boolean }): Promise<ServiceCatalogItem[]> => {
    return withOfflineFallback(
      async () => {
        const queryParams = new URLSearchParams();
        if (params?.category) queryParams.append('category', params.category);
        if (params?.isActive !== undefined) queryParams.append('isActive', String(params.isActive));

        const response = await api.get(`/service-catalog?${queryParams.toString()}`);
        return response.data;
      },
      () => readServiceCatalog({ category: params?.category }) as Promise<ServiceCatalogItem[]>,
    )();
  },

  getOne: async (id: string): Promise<ServiceCatalogItem> => {
    const response = await api.get(`/service-catalog/${id}`);
    return response.data;
  },

  create: async (data: Omit<ServiceCatalogItem, 'id' | 'createdAt' | 'updatedAt' | 'clinicId'>): Promise<ServiceCatalogItem> => {
    const response = await api.post('/service-catalog', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Omit<ServiceCatalogItem, 'id' | 'createdAt' | 'updatedAt' | 'clinicId'>>): Promise<ServiceCatalogItem> => {
    const response = await api.put(`/service-catalog/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/service-catalog/${id}`);
  },

  getTemplates: async (): Promise<ServiceCatalogItem[]> => {
    const response = await api.get('/service-catalog/templates');
    return response.data;
  },

  bulkImport: async (itemIds: string[]): Promise<ServiceCatalogItem[]> => {
    const response = await api.post('/service-catalog/bulk-import', { itemIds });
    return response.data;
  },
};

